// src/components/pages/BlogContent.jsx
import React, { useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Heading,
  Text,
  HStack,
  Button,
} from "@chakra-ui/react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { caseStudies } from "../../data/caseStudies";
import { companies } from "../../data/companies";
import CompanyCard from "../content/companylist/CompanyCard";

// very lightweight formatter: ### -> h3, ## -> h2, **bold**, _italic_/*italic*, [text](url),
// bullets, and groups Label: Value lines into a UL list.
function toHTML(block = "") {
  let s = String(block || "").replace(/\r\n/g, "\n");

  // Headings (order matters)
  s = s.replace(/^###\s*(.*)$/gm, "<h3>$1</h3>");
  s = s.replace(/^##\s*(.*)$/gm, "<h2>$1</h2>");

  // Links [text](url)
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Bold **text**
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  // Italic _text_ and *text* (single asterisks only)
  s = s.replace(/_(.+?)_/g, "<em>$1</em>");
  s = s.replace(/(^|[^*])\*(?!\*)([^*]+)\*(?!\*)/g, "$1<em>$2</em>");

  // Soft line breaks: "two+ spaces + newline" -> <br />
  s = s.replace(/ {2,}\n/g, "<br/>\n");

  // Convert bullet blocks: lines starting with - or *
  if (/^(\*|-)\s+/m.test(s)) {
    const lines = s.split("\n");
    let out = "";
    let inList = false;
    for (const line of lines) {
      const m = line.match(/^(\*|-)\s+(.*)$/);
      if (m) {
        if (!inList) {
          out += "<ul>";
          inList = true;
        }
        out += `<li>${m[2]}</li>`;
      } else {
        if (inList) {
          out += "</ul>";
          inList = false;
        }
        out += line.trim().length ? `${line}\n` : "\n";
      }
    }
    if (inList) out += "</ul>";
    s = out;
  }

  // KV lines — handle both "**Label:** Value" and "Label: Value"
  // 1) Bold label form (after bold pass, so it looks like "<strong>Label:</strong> Value")
  s = s.replace(
    /^<strong>([^:<]+):<\/strong>\s*(.+)$/gm,
    '<li class="kv"><strong>$1:</strong> $2</li>'
  );
  // 2) Plain "Label: Value" form
  s = s.replace(
    /^([A-Za-z][\w\s/&.\-]+):\s*(.+)$/gm,
    '<li class="kv"><strong>$1:</strong> $2</li>'
  );
  // Group consecutive KV items into one list
  s = s
    .replace(
      /(?:\s*<li class="kv">.*?<\/li>\s*)+/gs,
      (m) => `<ul class="kv-list">${m}</ul>`
    )
    .replace(/<\/ul>\s*<ul class="kv-list">/g, "");

  // Paragraphs: wrap double line breaks; leave block tags alone
  s = s
    .split(/\n{2,}/)
    .map((chunk) => {
      const t = chunk.trim();
      if (!t) return "";
      if (/^<(h2|h3|ul)/i.test(t)) return t; // already a block
      return `<p>${t}</p>`;
    })
    .join("");

  return s;
}

const FALLBACK_ACCENT = {
  TAG: "blue.600",
  WYNN: "purple.600",
  ATS: "orange.600",
  AMITY: "teal.600",
  TGC: "red.600",
};

function tokenToCssVar(token = "blue.600") {
  return `var(--chakra-colors-${String(token).replace(".", "-")})`;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildBrandColorMap(list) {
  const map = {};
  for (const c of list) {
    const token = c.accentColor || FALLBACK_ACCENT[c.key] || "blue.600";
    if (c.name) map[c.name] = token;
    if (c.shortName) map[c.shortName] = token;
  }
  return map;
}

// Only color brand names when they're already bolded (<strong>…</strong>)
function applyBrandColors(html, brandMap) {
  let out = html;
  for (const [name, token] of Object.entries(brandMap)) {
    const cssVar = tokenToCssVar(token);
    const re = new RegExp(
      `(<strong>\\s*)(${escapeRegExp(name)})(\\s*</strong>)`,
      "gi"
    );
    out = out.replace(re, `$1<span style="color:${cssVar}">$2</span>$3`);
  }
  return out;
}

export default function BlogContent() {
  const { slug } = useParams();

  const post = useMemo(
    () => caseStudies.find((p) => p.slug === slug) || null,
    [slug]
  );

  const company = useMemo(() => {
    if (!post?.companyKey) return null;
    const key = String(post.companyKey).toUpperCase();
    return companies.find((c) => String(c.key).toUpperCase() === key) || null;
  }, [post]);

  const cardBg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };
  const sub = { base: "gray.600", _dark: "gray.300" };

  if (!post) {
    return (
      <Container maxW="6xl" py={{ base: 10, md: 16 }}>
        <Stack spacing={4}>
          <Heading size="lg">Article not found</Heading>
          <Text color={sub}>We couldn’t find a post for “{slug}”.</Text>
          <HStack>
            <Button as={RouterLink} to="/tax-news" variant="outline">
              Back to Case Studies
            </Button>
            <Button as={RouterLink} to="/" colorScheme="blue">
              Home
            </Button>
          </HStack>
        </Stack>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero-ish header with overlay */}
      <Box
        position="relative"
        bgImage={post.hero || "/assets/hero/case-studies-hero.jpg"}
        bgPos="center"
        bgSize="cover"
        bgRepeat="no-repeat"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          bg: "rgba(0,0,0,0.55)",
        }}
      >
        <Container
          maxW="6xl"
          py={{ base: 10, md: 16 }}
          position="relative"
          zIndex={1}
          color="white"
        >
          <Stack spacing={3}>
            <Heading size="2xl">{post.title}</Heading>
            {post.excerpt ? (
              <Text maxW="3xl" opacity={0.9}>
                {post.excerpt}
              </Text>
            ) : null}
            <HStack spacing={3} wrap="wrap" opacity={0.85} fontSize="xl">
              {post.taxType ? <Text>Tax Type: {post.taxType}</Text> : null}
              {Array.isArray(post.issues) && post.issues.length ? (
                <Text>• Issues: {post.issues.join(", ")}</Text>
              ) : null}
              {post.resolution ? (
                <Text>• Resolution: {post.resolution}</Text>
              ) : null}
              {post.date ? (
                <Text>• {new Date(post.date).toLocaleDateString()}</Text>
              ) : null}
            </HStack>
          </Stack>
        </Container>
      </Box>

      <Container maxW="6xl" py={{ base: 8, md: 12 }}>
        <Stack spacing={{ base: 8, md: 10 }}>
          {/* Company card at top */}
          {company ? (
            <Box display="flex" justifyContent="center">
              <CompanyCard item={company} />
            </Box>
          ) : null}

          {/* Article body */}
          <Box
            borderWidth="1px"
            borderColor={border}
            bg={cardBg}
            rounded="lg"
            p={{ base: 6, md: 10 }}
          >
            <Box maxW="3xl" mx="auto">
              {Array.isArray(post.content) && post.content.length > 0 ? (
                <Box
                  borderWidth="1px"
                  borderColor={border}
                  bg={cardBg}
                  rounded="lg"
                  p={{ base: 6, md: 10 }}
                >
                  <Box maxW="3xl" mx="auto">
                    {post.content.map((block, i) => {
                      const html = toHTML(block);
                      const colored = applyBrandColors(
                        html,
                        buildBrandColorMap(companies)
                      );
                      return (
                        <React.Fragment key={i}>
                          <Box
                            className="blog-block"
                            // Force readable type directly on the block
                            fontSize={{ base: "22px", md: "28px" }}
                            lineHeight={1.95}
                            sx={{
                              // Headings
                              "& h2": {
                                fontSize: { base: "30px", md: "36px" },
                                fontWeight: 800,
                                lineHeight: 1.25,
                                mt: 6,
                                mb: 3,
                                color: { base: "gray.900", _dark: "gray.50" },
                              },
                              "& h3": {
                                fontSize: { base: "24px", md: "28px" },
                                fontWeight: 700,
                                lineHeight: 1.3,
                                mt: 5,
                                mb: 2,
                                color: { base: "gray.900", _dark: "gray.50" },
                              },

                              // Paragraphs & lists
                              "& p": {
                                margin: "0 0 18px 0",
                                color: { base: "gray.800", _dark: "gray.100" },
                              },
                              "& ul": {
                                paddingLeft: "1.5rem",
                                margin: "12px 0 18px",
                              },
                              "& li": {
                                listStyle: "disc",
                                marginBottom: "8px",
                              },

                              // Emphasis & links
                              "& strong": {
                                color: { base: "blue.800", _dark: "blue.200" },
                                fontWeight: 800,
                              },
                              "& em": {
                                color: { base: "gray.700", _dark: "gray.200" },
                              },
                              "& a": {
                                color: "blue.600",
                                textDecoration: "underline",
                              },
                            }}
                            dangerouslySetInnerHTML={{ __html: colored }}
                          />

                          {/* Add visible space between content blocks */}
                          {i < post.content.length - 1 ? (
                            <>
                              <br />
                              <br />
                            </>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Text color={sub} fontStyle="italic">
                  Full write-up coming soon.
                </Text>
              )}
            </Box>
          </Box>

          {/* Back link */}
          <HStack>
            <Button as={RouterLink} to="/tax-relief-blog" variant="outline">
              Back to Case Studies
            </Button>
          </HStack>
        </Stack>
      </Container>
    </Box>
  );
}
